import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Button,
    InputGroup,
    Label
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { AiOutlineSync } from 'react-icons/ai';
import Breadcrumbs from "components/Common/Breadcrumb";
import ToastDialog from "components/Common/ToastDialog";
import TableContainerV1 from "components/Common/TableContainerV1";
import { getMTDSalesReport, getMTDSalesReportSync } from "../../../helpers/UAM/report.helper"
import * as XLSX from 'xlsx';
import { formatDate, formatDateTime, formatNumberWithCommas, isPermission } from "common/utility";
import { getApiLogs } from "http/apiLogs/apiLogs";
import moment from 'moment';

const MTDSalesReport = () => {
    document.title = "MTD Sales Report | DMS";
    const [mtdSalesReport, setMtdSalesReport] = useState([])
    const [pagination, setPagination] = useState({ pageIndex: 0, pageCount: 0, totalRecords: 0 })
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchValue, setSearchValue] = useState("");
    const [columnId, setColumnId] = useState("id");
    const [sortingOrderStatus, setSortingOrderStatus] = useState("desc");
    const [filters, setFilters] = useState(null);
    const [filtersForSync, setFiltersForSync] = useState(null);
    const [sortingOptions, setSortingOptions] = useState({
        column: "",
        isAsc: false,
        optionalColumn: [],
    });
    const flatpickrRef = useRef(null);
    const user = JSON.parse(localStorage.getItem("user"));
    const [totalData, setTotalData] = useState('');
    const [lastSynced, setLastSynced] = useState(null);
    const [showFilter, setShowFilter] = useState(false);
    const flatpickrRef2 = useRef(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const [toastInfo, setToastInfo] = useState({ showToast: false })
    const [syncPermission, setSyncPermission] = useState(false);

    const columns = useMemo(
        () => [
            {
                Header: 'Customer',
                accessor: 'customerId',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Customer Name',
                accessor: 'customerName',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Material',
                accessor: 'materialNo',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Material Description',
                accessor: 'materialDescription',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Plant',
                accessor: 'plant',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Quantity',
                accessor: 'quantity',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Net value',
                accessor: 'netValue',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? <p style={{ width: '150px' }}>{formatNumberWithCommas(cellProps.value)}</p> : "";
                }
            },
            {
                Header: 'Tax amount',
                accessor: 'taxAmount',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? <p style={{ width: '150px' }}>{formatNumberWithCommas(cellProps.value)}</p> : "";
                }
            },
            {
                Header: 'Sales Document',
                accessor: 'salesDocument',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Sales Document Item',
                accessor: 'salesDocItem',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Invoice No',
                accessor: 'invoiceNo',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'ODN No.',
                accessor: 'odnNo',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Zone',
                accessor: 'zone',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Purchase Order No',
                accessor: 'purchaseOrderNo',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? cellProps.value : "";
                }
            },
            {
                Header: 'Invoice Date',
                accessor: 'invoiceDate',
                filterable: true,
                Cell: (cellProps) => {
                    return cellProps.value ? formatDate(cellProps.value) : "";
                }
            },
        ],
        []
    );

    useEffect(() => {
        const [startDate, endDate] = getFinancialYearDates();
        let objFilter = {
            startDate: startDate,
            endDate: endDate
        }
        setFilters(objFilter);
        fetchMTDSalesReport({ page: 1, limit: pageSize, searchKey: '', columnName: columnId, sortingOrder: sortingOrderStatus, filterType: objFilter })
        fetchLastSynced()
        checkPermission()
    }, [])

    const checkPermission = async () => {
        let isSyncPermission = await isPermission('sync', 'create_permission')
        setSyncPermission(isSyncPermission)
    }

    const fetchMTDSalesReport = (data) => {
        getMTDSalesReport(data)
            .then((response) => {
                if (response.status && response.code == 200) {
                    setMtdSalesReport(response.data.data);
                    setTotalData(calculateZoneTotals(response.data.data))
                    const { totalItems, totalPages, currentPage } = response.data
                    setPagination({
                        pageIndex: currentPage,
                        pageCount: totalPages,
                        totalRecords: totalItems,
                    })
                } else {
                    setPagination({ pageIndex: 0, pageCount: 0, totalRecords: 0 })
                    setMtdSalesReport([])
                }
            })
            .catch((err) => { console.log(err) })
    }

    const handlePageChange = (page, pageSize, searchKey = searchValue, columnName = columnId, sortingOrder = sortingOrderStatus, filterType = filters) => {
        setPageSize(pageSize);
        setCurrentPage(page)
        fetchMTDSalesReport({ page, limit: pageSize, searchKey, columnName, sortingOrder, filterType })
    };

    const handleSortColumn = (option) => {
        setSortingOptions(option);
        const columnName = option.column;
        setColumnId(columnName);
        const sortingOrder = option.isAsc ? "asc" : "desc";
        setSortingOrderStatus(sortingOrder);
        handlePageChange(currentPage, pageSize, searchValue, columnName, sortingOrder);
    };

    const handleSearchFilter = (value, pageSize) => {
        const newValue = value !== undefined ? value : "";
        setSearchValue(newValue);
        handlePageChange(1, pageSize, newValue);
    };

    const getFinancialYearDates = () => {
        const today = new Date();
        let startYear, endYear;

        if (today.getMonth() + 1 >= 4) {
            startYear = today.getFullYear();
            endYear = today.getFullYear() + 1;
        } else {
            startYear = today.getFullYear() - 1;
            endYear = today.getFullYear();
        }

        const startDate = new Date(`${startYear}-04-01T00:00:00.000Z`);
        const endDate = new Date(`${endYear}-03-31T23:59:59.999Z`);
        return [startDate.toISOString(), endDate.toISOString()];
    };

    const handleClearSearch = () => {
        setSearchValue("");
        setFilters(null)
        flatpickrRef?.current?.flatpickr?.clear();
        handlePageChange(1, pageSize, "", 'id', 'desc', null);
    };

    const handleDateFilter = () => {
        handlePageChange(1, pageSize, searchValue, columnId, sortingOrderStatus, filters)
    }

    const handleDateChange = (selectedDates) => {
        if (selectedDates.length === 2) {
            setFilters({
                startDate: selectedDates[0],
                endDate: selectedDates[1],
            })
        }
    };

    const downloadExcel = () => {
        let dataWithModifiedCol = mtdSalesReport.map(report => {
            let newReport = {
                "Customer": report.customerId,
                "Customer Name": report.customerName,
                "Material": report.materialNo,
                "materialDescription": report.materialDescription,
                "Plant": report.plant,
                "Quantity": report.quantity,
                "Net value": formatNumberWithCommas(report.netValue),
                "Tax amount": formatNumberWithCommas(report.taxAmount),
                "Sales Document": report.salesDocument,
                "Sales Document Item": report.salesDocItem,
                "Invoice No": report.invoiceNo,
                "ODN No.": report.odnNo,
                "Zone": report.zone,
                "Purchase Order No": report.purchaseOrderNo,
                "Invoice Date": formatDate(report.invoiceDate),
            }
            return newReport
        })
        const totalNetValue = mtdSalesReport.reduce((sum, report) => sum + report.netValue, 0);
        const totalTaxAmount = mtdSalesReport.reduce((sum, report) => sum + report.taxAmount, 0);

        dataWithModifiedCol.push({
            "Customer": "Total",
            "Net value": formatNumberWithCommas(totalNetValue),
            "Tax amount": formatNumberWithCommas(totalTaxAmount),
        });
        // Create a new workbook
        const workbook = XLSX.utils.book_new();
        const sheetData = XLSX.utils.json_to_sheet(dataWithModifiedCol);
        // Add the sheet to the workbook
        XLSX.utils.book_append_sheet(workbook, sheetData, 'MTDSalesReport');
        // Save the workbook as an Excel file
        XLSX.writeFile(workbook, 'MTD_Sales_Report.xlsx');
    };

    const toggleFilter = () => {
        setShowFilter(!showFilter)
    }

    const handleDateChangeForSync = (selectedDates) => {
        if (selectedDates.length === 2) {
            setFiltersForSync({
                fromDate: selectedDates[0],
                toDate: selectedDates[1],
            })
        }
    };

    const handleClearFilters = () => {
        setFiltersForSync(null)
        setShowFilter(false)
        if (flatpickrRef2 && flatpickrRef2.current) {
            flatpickrRef2.current.flatpickr.clear();
        }
    };

    const handleSync = async () => {
        setIsSyncing(true);
        setShowFilter(false)
        if (!filtersForSync?.fromDate || !filtersForSync?.toDate) {
            setToastInfo({
                message: "Please select date range",
                showToast: true,
                role: "error",
            })
            setIsSyncing(false);
            return
        }
        try {
            let obj = {
                fromDate:moment(filtersForSync?.fromDate).format('YYYYMMDD'),
                toDate:moment(filtersForSync?.toDate).format('YYYYMMDD')
            }
            let res = await getMTDSalesReportSync(obj)
            if (res.status) {
                setToastInfo({
                    message: res.message,
                    showToast: true,
                    role: "success",
                })
                setIsSyncing(false);
                const [startDate, endDate] = getFinancialYearDates();
                let objFilter = {
                    startDate: startDate,
                    endDate: endDate
                }
                setFilters(objFilter);
                fetchMTDSalesReport({ page: 1, limit: pageSize, searchKey: '', columnName: columnId, sortingOrder: sortingOrderStatus, filterType: objFilter })
                fetchLastSynced()
            } else {
                setToastInfo({
                    message: "Something went wrong",
                    showToast: true,
                    role: "error",
                })
                setIsSyncing(false);
                fetchLastSynced()
            }

        } catch (err) {
            setToastInfo({
                message: "Something went wrong",
                showToast: true,
                role: "error",
            })
            setIsSyncing(false);
            fetchLastSynced()
        }
    }

    const buttonSection = (
        <div className="text-sm-end">
            <div className="d-lg-flex justify-content-end">
                {mtdSalesReport.length > 0 && (<Button
                    type="button"
                    color="success"
                    className="btn-rounded mb-2 me-2"
                    onClick={downloadExcel}
                >
                    <i className="bx bx-archive-in me-1" />
                    {'Download'}
                </Button>)}
                {syncPermission && (<div className="text-md-end">
                    <Button
                        type="button"
                        color="success"
                        className="btn-rounded mb-2 me-2"
                        onClick={toggleFilter}
                    >
                        <AiOutlineSync size={15} className={isSyncing ? 'rotating' : ''} /> Sync Data
                    </Button>
                </div>)}
            </div>

            {showFilter && (
                <div className="filter-container2" style={{ minHeight: '50px' }}>
                    <div className="mb-1 d-flex justify-content-between align-items-center text-center">
                        <h5 style={{ margin: 0 }}>Filter</h5>
                        <button
                            type="button"
                            onClick={() => setShowFilter(false)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                fontSize: '1.2rem',
                                cursor: 'pointer',
                            }}
                        >
                            ×
                        </button>
                    </div>
                    <div className="d-flex align-items-center justify-content-start">
                        <InputGroup style={{ minWidth: '250px' }}>
                            <Flatpickr
                                ref={flatpickrRef2}
                                className="form-control"
                                placeholder="Start Date - End Date"
                                options={{
                                    mode: 'range',
                                    dateFormat: 'd-m-Y',
                                }}
                                value={filtersForSync?.fromDate && filtersForSync?.toDate ? [filtersForSync?.fromDate, filtersForSync?.toDate] : []}
                                onChange={handleDateChangeForSync}
                            />
                            <Button variant="primary"
                                onClick={handleSync}
                                style={{ backgroundColor: "#344fa0", border: "none" }}>
                                Go
                            </Button>
                        </InputGroup>
                    </div>
                    <div className="d-flex flex-row justify-content-end gap-2 mt-3">
                        <Button
                            type="button"
                            color="light"
                            className="btn-rounded"
                            onClick={handleClearFilters}
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            )}

            {lastSynced && <p className="d-flex justify-content-end ">Last Synced: {lastSynced}</p>}
        </div>
    );

    const calculateZoneTotals = (data) => {
        let netValue = 0
        let taxAmount = 0
        data.forEach(item => {
            netValue += item.netValue || 0
            taxAmount += item.taxAmount || 0
        });
        return (<tr>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}>{'Total'}</td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}></td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}></td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}></td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}></td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}></td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}>{formatNumberWithCommas(netValue)}</td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}>{formatNumberWithCommas(taxAmount)}</td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}></td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}></td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}></td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}></td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}></td>
            <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}></td>
        </tr>)
    };

    const fetchLastSynced = async () => {
        try {
            let res = await getApiLogs({ masterName: 'mtd_pending_sale_report' })
            if (res.status) {
                setLastSynced(formatDateTime(res.data.initialDateTime))
            }
        } catch (err) {
            console.error(err)
        }
    }

    const customeFilterSection = (
        <div className="d-flex align-items-center justify-content-start">
            <InputGroup style={{ minWidth: '250px' }}>
                <Flatpickr
                    ref={flatpickrRef}
                    className="form-control"
                    placeholder="Start Date - End Date"
                    options={{
                        mode: 'range',
                        dateFormat: 'd-m-Y',
                    }}
                    value={filters?.startDate && filters?.endDate ? [filters?.startDate, filters?.endDate] : []}
                    onChange={handleDateChange}
                />
                <Button variant="primary"
                    onClick={handleDateFilter}
                    style={{ backgroundColor: "#344fa0", border: "none" }}>
                    Go

                </Button>
            </InputGroup>
        </div>
    )

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Reports" breadcrumbItem="MTD Sales Report" />
                <Row>
                    <Col xs="12">
                        <Card>
                            <CardBody>
                                <TableContainerV1
                                    columns={columns}
                                    data={mtdSalesReport}
                                    pagination={pagination}
                                    handlePageChange={handlePageChange}
                                    searchHelperText="Search by Customer Name, Material, Material Description, Plant"
                                    handleSearchFilter={handleSearchFilter}
                                    customFilterSection={customeFilterSection}
                                    customSection={buttonSection}
                                    sortOptions={sortingOptions}
                                    handleClearSearch={handleClearSearch}
                                    handleSortColumn={handleSortColumn}
                                    isTotal={true}
                                    TotalData={totalData}
                                    className="custom-header-css"
                                />
                            </CardBody>
                        </Card>

                    </Col>
                </Row>
            </Container>
            <ToastDialog
                message={toastInfo.message}
                showToast={toastInfo.showToast}
                role={toastInfo.role}
                onCloseFn={() => setToastInfo({ showToast: false })}
            />
        </div>
    );
};

export default MTDSalesReport;
